import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2, Sunrise, Sunset, Waves } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtTime } from "../lib/api";
import { RatingBlock, ratingTier } from "../components/Badges";
import { WeatherGrid } from "../components/WeatherGrid";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const BAR = { hot: "bg-[#FF4D00]", mid: "bg-[#EAB308]", low: "bg-[#3F3F46]" };

function HourChart({ hours, best }) {
  return (
    <div className="brut-card p-4" data-testid="spot-hour-chart">
      <div className="flex items-end gap-[3px] h-32">
        {hours.map((h) => (
          <div key={h.time} className="flex-1 flex flex-col items-center justify-end h-full" title={`${fmtTime(h.time)} · ${h.rating}/10`}>
            <div className={`w-full ${BAR[ratingTier(h.rating)]} ${h.time === best?.time ? "outline outline-2 outline-white" : ""}`}
              style={{ height: `${h.rating * 10}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-500 mt-2">
        <span>Now</span><span>+12h</span><span>+24h</span>
      </div>
    </div>
  );
}

function Tides({ tide }) {
  if (!tide.available) {
    return <div className="brut-card p-4 text-sm text-zinc-400" data-testid="spot-no-tide">No tide data for this spot — rating uses light, wind and pressure only.</div>;
  }
  return (
    <div className="brut-card p-4 space-y-3" data-testid="spot-tides">
      <p className="text-xs text-zinc-400">NOAA station: {tide.station_name} ({tide.distance_mi} mi)</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {tide.today_events.map((e) => (
          <div key={e.time} className={`border-2 p-3 ${e.type === "High" ? "border-[#FF4D00]" : "border-[#27272A]"}`} data-testid={`spot-tide-event-${e.time}`}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-1"><Waves size={12} /> {e.type}</div>
            <div className="font-display text-lg">{fmtTime(e.time)}</div>
            <div className="text-xs text-zinc-400">{e.height_ft} ft</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SpotDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/spots/${id}/conditions`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [id]);

  const remove = async () => {
    try { await api.delete(`/spots/${id}`); toast.success("Spot removed"); nav("/spots"); } catch (e) { toast.error(errMsg(e)); }
  };

  const back = (
    <Link to="/spots" className="inline-flex items-center gap-2 text-sm font-bold uppercase text-zinc-400 hover:text-white" data-testid="spot-detail-back-link">
      <ArrowLeft size={16} /> Spots
    </Link>
  );
  if (error) return <div className="space-y-4">{back}<p className="text-[#EF4444]" data-testid="spot-detail-error">{error}</p></div>;
  if (!data) return <div className="space-y-4">{back}<p className="text-zinc-500" data-testid="spot-detail-loading">Reading the water…</p></div>;

  const { spot, conditions: c } = data;
  const tideNow = c.tide.available ? c.tide.now : null;

  return (
    <div className="space-y-6">
      {back}
      <div className="brut-card-hot p-5 flex items-center gap-5">
        <RatingBlock rating={c.best_today?.rating} size="lg" testId="spot-detail-today-rating" />
        <div className="min-w-0">
          <span className="brut-label !mb-1">Today's best bite</span>
          <h1 className="font-display text-3xl uppercase leading-none truncate" data-testid="spot-detail-name">{spot.name}</h1>
          <p className="text-sm text-zinc-400 mt-2">Peak at {fmtTime(c.best_today?.time)} · now {c.rating_now ?? "–"}/10</p>
        </div>
      </div>

      <div className="bg-[#FF4D00] text-black p-5 border-2 border-black shadow-[4px_4px_0_0_#F4F4F5]" data-testid="spot-best-next-24h">
        <span className="text-xs font-bold uppercase tracking-widest">Best time · next 24 hours</span>
        <div className="font-display text-4xl uppercase mt-1">{fmtTime(c.best_next_24h?.time)}</div>
        <div className="text-sm font-bold">Rating {c.best_next_24h?.rating ?? "–"}/10</div>
      </div>

      <HourChart hours={c.hours} best={c.best_next_24h} />

      <div className="space-y-3">
        <h2 className="brut-h2">Right now</h2>
        <WeatherGrid w={c.current} tide={tideNow ? { available: true, ...tideNow } : { available: false }} prefix="spot-weather" />
        <div className="flex gap-6 text-xs text-zinc-400">
          <span className="flex items-center gap-1"><Sunrise size={14} /> {fmtTime(c.sunrise)}</span>
          <span className="flex items-center gap-1"><Sunset size={14} /> {fmtTime(c.sunset)}</span>
          <span>{c.timezone}</span>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="brut-h2">Today's tides</h2>
        <Tides tide={c.tide} />
      </div>

      <p className="text-xs text-zinc-500">Bite rating blends time of day (dawn/dusk best), tide movement, wind and barometric pressure trend.</p>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="brut-btn-ghost w-full !border-[#EF4444] !text-[#EF4444] hover:!bg-[#EF4444] hover:!text-white" data-testid="spot-delete-btn">
            <Trash2 size={16} /> Remove spot
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent className="rounded-none border-2 border-[#FF4D00]">
          <AlertDialogHeader><AlertDialogTitle className="font-display uppercase">Remove {spot.name}?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="spot-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-[#EF4444] text-white" onClick={remove} data-testid="spot-delete-confirm-btn">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
